import {Fragment, useCallback} from 'react';

import styled from 'styled-components';
import {useDispatch} from 'react-redux';

import {getLocalizationService} from '~/shared/services/localisationService';
import Button from '~/shared/components/Button';
import {Fieldset, HiddenLegend} from '~/shared/components/styled';
import Checkbox from '~/shared/components/Checkbox';
import {media} from '~/shared/theme/media';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {trackEvent} from '~/shared/services/analytics';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import {CommunicationMethodName} from '~/shared/services/bffService/api/interfaces/payloads';
import {ToggleName} from '~/shared/types/personalArea';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import useIsB2BUser from '~/common/hooks/useIsB2BUser';
import useSettings from '~/personalArea/components/Sections/Settings/useSettings';
import {smallRegular} from '~/shared/theme/typography';
import useIsGovUser from '~/common/hooks/useIsGovUser';
import BrandLogo from '~/shared/components/BrandLogo';

import {ErrorText, LoaderContainer, Row, Title} from '../Shared';

import {SnackbarView, SnackbarProvider} from './Snackbar';
import CheckCircleIcon from './checkCircle.svg?react';
import ToggleSettingWithError from './ToggleSettingWithError';

const Root = styled.div`
  height: 100%;
  padding: 24px 16px 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  ${media.minLargeMobile`
    display: block;
    height: auto;
    padding: 32px;
  `}
  ${media.minLargeTablet`
    padding: 40px;
  `}
`;

const MobileContentWrapper = styled.div`
  width: 100%;
`;

const ColOnMinLargeTablet = styled(Row)`
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  ${media.minLargeMobile`
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
    gap: 0;
  `}
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Label = styled.label``;

const StyledCheckbox = styled(Checkbox)`
  flex: none;
  margin: 0;

  path {
    &:first-child {
      fill: ${({theme}) => theme.colors.contentBrand};
    }
  }

  body.contrast-filter & path {
    fill: inherit;
    transform: scale(0.95);
  }

  .checkbox-rectangle[style*="border"] {
    border: 1px solid ${({theme}) => theme.colors.borderForm} !important;
  }

  .checkbox-rectangle {
    border-radius: 4px;
  }
`;

const StyledFieldset = styled(Fieldset)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  ${flipOnLTR`
    margin-left: 34px;  
  `}
`;

const DeleteAccountButton = styled(Button)`
  align-self: flex-start;
  font-size: 14px;
  color: ${({theme}) => theme.colors.contentLink};
`;

const SnackbarContent = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const SnackbarUnsubscribeText = styled.p`
  ${smallRegular}
  flex: 1;
`;

type SettingsProps = {
  isMinLargeMobile?: boolean;
};

const SettingsComponent = ({isMinLargeMobile}: SettingsProps) => {
  const dispatch = useDispatch();
  const {t} = getLocalizationService();
  const isB2bUser = useIsB2BUser();
  const isGovUser = useIsGovUser();
  const {settingControllers, isLoaded, hasCheckboxesError} = useSettings();

  const handleDeleteAccount = useCallback(() => {
    trackEvent('hasClickedDeleteAccount');
    dispatch(
      setCurrentModal('remove_account_modal', {
        hideCloseButton: true,
      }),
    );
  }, [dispatch]);

  if (!isLoaded) {
    return (
      <LoaderContainer>
        <SimpleSpinnerLoader variant="pieBig" />
      </LoaderContainer>
    );
  }

  const Wrapper = isMinLargeMobile ? Fragment : MobileContentWrapper;
  
  return (
    <Root>
      <Wrapper>
        <Title>{t('personal_area_page_settings_tab')}</Title>
        <StyledForm>
          <Row>
            <ToggleSettingWithError
              label={t('send_me_emails_about_updates_and_promotions_from_brandName')}
              isActive={settingControllers[ToggleName.PROMOTIONS].isActive}
              hasError={settingControllers[ToggleName.PROMOTIONS].hasError}
              onToggle={settingControllers[ToggleName.PROMOTIONS].handleChange}
              dataTestId="email-updates-toggle"
              isMinLargeMobile={isMinLargeMobile}
            />
          </Row>
          <Row>
            <ToggleSettingWithError
              label={t('after_order_feedback_email')}
              isActive={settingControllers[ToggleName.RES_REVIEW].isActive}
              hasError={settingControllers[ToggleName.RES_REVIEW].hasError}
              onToggle={settingControllers[ToggleName.RES_REVIEW].handleChange}
              dataTestId="after-order-feedback-toggle"
              isMinLargeMobile={isMinLargeMobile}
            />
          </Row>
          {isB2bUser && (
            <ColOnMinLargeTablet>
              <Label>{t('keep_me_updated_by')}</Label>
              <StyledFieldset>
                <HiddenLegend>{t('keep_me_updated_by')}</HiddenLegend>
                <StyledCheckbox
                  data-test-id="emailCheckbox"
                  outlined
                  label={t('email_2')}
                  id={CommunicationMethodName.EMAIL}
                  name={CommunicationMethodName.EMAIL}
                  onValueChange={settingControllers[CommunicationMethodName.EMAIL].handleChange}
                  value={settingControllers[CommunicationMethodName.EMAIL].isActive}
                />
                <StyledCheckbox
                  data-test-id="smsCheckbox"
                  outlined
                  label={t('sms')}
                  id={CommunicationMethodName.SMS}
                  name={CommunicationMethodName.SMS}
                  onValueChange={settingControllers[CommunicationMethodName.SMS].handleChange}
                  value={settingControllers[CommunicationMethodName.SMS].isActive}
                />
                <StyledCheckbox
                  data-test-id="pushCheckbox"
                  outlined
                  label={t('push_notifications')}
                  id={CommunicationMethodName.PUSH}
                  name={CommunicationMethodName.PUSH}
                  onValueChange={settingControllers[CommunicationMethodName.PUSH].handleChange}
                  value={settingControllers[CommunicationMethodName.PUSH].isActive}
                />
              </StyledFieldset>
            </ColOnMinLargeTablet>
          )}
          {hasCheckboxesError && <ErrorText>{t('something_went_wrong_try_again_later')}</ErrorText>}
          {!isB2bUser && <DeleteAccountButton data-test-id="deleteAccountButton" onClick={handleDeleteAccount}>{t('delete_account')}</DeleteAccountButton>}
        </StyledForm>
      </Wrapper>
      <SnackbarView>
        <SnackbarContent>
          <CheckCircleIcon />
          <SnackbarUnsubscribeText>{t(settingControllers[ToggleName.PROMOTIONS].isActive ? 'your_in_youve_successfully_joined_brandName_promotions' : 'sorry_to_see_you_go_youve_been_from_brandName_promotions')}</SnackbarUnsubscribeText>
        </SnackbarContent>
      </SnackbarView>
      {!isMinLargeMobile && <BrandLogo isGov={isGovUser} />}
    </Root>
  );
};

const Settings = (props: SettingsProps) => {
  return (
    <SnackbarProvider>
      <SettingsComponent {...props} />
    </SnackbarProvider>
  );
};

export default Settings;
